import {useMemo} from 'react';

import {NucleotideType} from 'biojs/BioConstants';
import {SequenceType, SequenceTopology} from 'constants/SequenceConstants';
import {
  FragmentProductionMethod,
  MethodData,
  BulkAssemblyMethod,
  BasicPolymerInfo,
} from 'molbio/bulk-assembly/BulkAssemblyConstants';
import {NO_ENZYME_SENTINEL} from 'molbio/enzymes/EnzymeConstants';
import {getCuts, getEnzyme} from 'molbio/enzymes/EnzymeUtil';

export function getPreferredRestrictionEnzymeId(assemblyMethod: MethodData): number | null {
  if (assemblyMethod.type === BulkAssemblyMethod.GOLDEN_GATE) {
    return assemblyMethod.parameters.defaultRestrictionEnzymeId;
  }
  return null;
}

export type RequiredEnzymesDetails = {isStartEnzymeRequired: boolean; isEndEnzymeRequired: boolean} | null;

export function areEnzymesRequired(
  binIndex: number | null,
  restrictionEnzymeInfo: RestrictionEnzymeInfo,
  constructTopology: SequenceTopology,
  numBins: number,
  fragmentProductionMethod: FragmentProductionMethod
): RequiredEnzymesDetails {
  if (fragmentProductionMethod !== FragmentProductionMethod.EXISTING_CUT_SITES) {
    // If the restriction enzyme is not specified (unable to search for cut sites), neither enzyme is required.
    return {
      isStartEnzymeRequired: false,
      isEndEnzymeRequired: false,
    };
  }
  if (
    restrictionEnzymeInfo.restrictionEnzymeId === NO_ENZYME_SENTINEL ||
    restrictionEnzymeInfo.cutsiteIndices == null ||
    restrictionEnzymeInfo.cutsiteIndices.length > 1 ||
    constructTopology === SequenceTopology.CIRCULAR
  ) {
    // If the restriction enzyme is not specified, there is more than one cut site, or the construct is circular, the enzyme is always required
    return {
      isStartEnzymeRequired: true,
      isEndEnzymeRequired: true,
    };
  } else if (binIndex == null) {
    // If the bin is not specified at this point, we cannot say whether the enzyme is required.
    return null;
  } else if (binIndex === 0) {
    // If this is the first bin, the 5' enzyme is required and the 3' enzyme is not.
    return {
      isStartEnzymeRequired: false,
      isEndEnzymeRequired: true,
    };
  } else if (binIndex === numBins - 1) {
    // If this is the last bin, the 3' enzyme is required and the 5' enzyme is not.
    return {
      isStartEnzymeRequired: true,
      isEndEnzymeRequired: false,
    };
  } else {
    // Otherwise, both enzymes are required.
    return {
      isStartEnzymeRequired: true,
      isEndEnzymeRequired: true,
    };
  }
}

export type RestrictionEnzymeInfo =
  | {restrictionEnzymeId: number; cutsiteIndices: Array<number>}
  | {restrictionEnzymeId: null | typeof NO_ENZYME_SENTINEL; cutsiteIndices: null};

export function getRestrictionEnzymeInfo(
  restrictionEnzymeId: number | null,
  sequenceBases: string,
  circular: boolean
): RestrictionEnzymeInfo {
  if (restrictionEnzymeId == null || restrictionEnzymeId === NO_ENZYME_SENTINEL) {
    return {
      restrictionEnzymeId,
      cutsiteIndices: null,
    };
  } else if (sequenceBases.length === 0) {
    return {restrictionEnzymeId, cutsiteIndices: []};
  } else {
    const cutsiteIndices = getCuts(
      getEnzyme(restrictionEnzymeId),
      sequenceBases,
      // Right now, enzyme-based assemblies are only permitted on long DNA fragments.
      NucleotideType.DNA,
      SequenceType.DOUBLE_STRANDED,
      circular
    ).map(({position}) => position);
    return {
      restrictionEnzymeId,
      cutsiteIndices,
    };
  }
}

export function useRestrictionEnzymeInfo(
  restrictionEnzymeId: number | null,
  basicPolymerInfo?: BasicPolymerInfo
): RestrictionEnzymeInfo {
  const bases = basicPolymerInfo?.residues ?? '';
  const circular = basicPolymerInfo?.circular ?? false;
  return useMemo(
    () => getRestrictionEnzymeInfo(restrictionEnzymeId, bases, circular),
    [restrictionEnzymeId, bases, circular]
  );
}
